/**
 * A Logger class to handle logging messages to the console based on the specified log level.
 *
 * The log level can be controlled by setting `window.trumpf.ux.logLevel`
 * Developers can overwrite `window.trumpf.ux.logLevel` by setting the value before the library is loaded`.
 * The property can also be changed at runtime to adjust the log level dynamically by calling `window.trumpf.ux.setLogLevel()
 * @internal
 */
class Logger {
    #logLevel: string;
    #levels: string[];
    #currentLevelIndex: number;

    constructor() {
        this.#logLevel = window.trumpf?.ux?.logLevel || "info";
        this.#levels = ["debug", "info", "warn", "error"];
        this.#currentLevelIndex = this.#levels.indexOf(this.#logLevel);
    }

    /**
     * Logs a debug message to the console if the current log level allows it.
     * @param message - The message(s) to log.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    debug(...message: any[]) {
        if (this.#shouldLog(this.#levels.indexOf("debug"))) {
            // eslint-disable-next-line no-console
            console.debug(message);
        }
    }

    /**
     * Logs an info message to the console if the current log level allows it.
     * @param message - The message(s) to log.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    info(...message: any[]) {
        if (this.#shouldLog(this.#levels.indexOf("info"))) {
            // eslint-disable-next-line no-console
            console.info(message);
        }
    }

    /**
     * Logs a warning message to the console if the current log level allows it.
     * @param message - The message(s) to log.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    warn(...message: any[]) {
        if (this.#shouldLog(this.#levels.indexOf("warn"))) {
            // eslint-disable-next-line no-console
            console.warn(message);
        }
    }

    /**
     * Logs an error message to the console if the current log level allows it.
     * @param message - The message(s) to log.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error(...message: any[]) {
        if (this.#shouldLog(this.#levels.indexOf("error"))) {
            // eslint-disable-next-line no-console
            console.error(message);
        }
    }

    /**
     * Determines if a message should be logged based on the current log level.
     * @param messageLevelIndex - The index of the message's log level.
     * @returns `true` if the message should be logged, otherwise `false`.
     */
    #shouldLog(messageLevelIndex: number) {
        return messageLevelIndex >= this.#currentLevelIndex;
    }
}

export const logger = new Logger();
